import { getDownloadMouseScreenshotQueryOptions } from '@/_api/actions/downloadMouseScreenshot';
import { getQueryClient } from '@/_lib/getQueryClient';
import { trackEvent } from '@/_lib/tracking';
import { DefaultAction } from '@/_types/Action';

export const downloadUrl = async (filename: string, url: string, isCloudImgUrl?: boolean): Promise<void> => {
  // no-cache required because otherwise when the images are loaded
  // in the screenshot img tag they are loaded WITHOUT a CORS header.
  // so the fetch request fails. With no cache, they are refetched
  // with a proper CORS header.
  const image = await fetch(url, { cache: 'no-cache' });
  const imageBlob = await image.blob();
  const imageURL = URL.createObjectURL(imageBlob);

  const link = document.createElement('a');

  link.href = imageURL;
  if (isCloudImgUrl) {
    /*
     * This is a CloudImage URL, so the URL looks quite different. It will always return a png. So we can just hard-code the extension.
     */
    link.download = `${filename}.png`;
  } else {
    const afterPeriod = url.split('.')[url.split('.').length - 1];
    const extension = afterPeriod?.split('?')[0];

    link.download = `${filename}.${extension}`;
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getActionScreenshotUrl = (action: DefaultAction): string | null => {
  return action.user_cropped_screenshot?.url || action.screenshot_url || action.external_screenshot_url || null;
};

export const downloadImage = async ({ action, fileName }: { action: DefaultAction; fileName: string }): Promise<void> => {
  const screenshotUrl = getActionScreenshotUrl(action);

  if (!screenshotUrl) return Promise.resolve();
  if (action.kind === 'stack') {
    trackEvent('downloaded_step_file', { file_id: action.id, location: 'step_tridot' });
    return downloadUrl(`${fileName} - Step ${action.sort_order}`, screenshotUrl);
  }
  const queryClient = getQueryClient();
  const data = await queryClient.fetchQuery(getDownloadMouseScreenshotQueryOptions({ actionId: action.id }));

  if (!data) return Promise.resolve();
  trackEvent('downloaded_step_image', { file_id: action.id, location: 'step_tridot' });
  return downloadUrl(`${fileName} - Step ${action.sort_order}`, data.screenshot_url, true);
};

export const downloadFullImage = ({ action, fileName }: { action: DefaultAction; fileName: string }) => {
  if (!action.screenshot_url) return;
  downloadUrl(`${fileName} - Step ${action.sort_order}`, action.screenshot_url);
  trackEvent('downloaded_full_step_image', { file_id: action.id, location: 'step_tridot' });
};

export const isClickTargetPositionDefined = (position: { x: number; y: number }) =>
  typeof position?.x !== 'undefined' && typeof position?.y !== 'undefined';

export const isActionGuideMeEligible = (action: DefaultAction) => {
  // the very first instruction of "navigate to [url]" is "instruction with a null instruction_type"
  const isNavigateToInstruction = action.kind === 'instruction' && !action.instruction_type;

  const isUserAction =
    action.kind === 'mouse_click_action' ||
    action.kind === 'keyboard_action' ||
    action.kind === 'keyboard_combination_action' ||
    action.kind === 'keyboard_sequence_action';

  return isNavigateToInstruction || isUserAction;
};
